import React from 'react';
import {
  Button,
  Card,
  HeadingText,
  ParagraphText,
  queryHelpers,
  StackItem,
  StackLayout,
  useMediaQuery,
  UtilityText,
} from '@leagueplatform/genesis-core';
import {
  MasonryEngineNodeAction,
  type MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';

import { InternalOrExternalLink } from '@leagueplatform/web-common-components';
import { SquareImageContainer } from '#common/square-image-container/square-image-container.component.tsx';
import { BodyTextHtmlTagOptions } from '#utils/body-text-html-tag-options.ts';
import { MasonryEngineNode } from '@leagueplatform/masonry-engine';
import {
  MasonryDeeplinkAction,
  MasonryDismissAction,
} from '#types/masonry-driver-node-actions.ts';
import { getButtonIconToken, Indicator } from '#utils/icon-mapping.ts';
import { formatClassNames } from '../../utils/format-masonry-class-name.util';
import { useInvokeActionWithLoadingState } from '../../hooks/use-invoke-action-with-loading-state.hook';
import { DismissButton } from '../common/dismiss-button/dismiss-button';

type MasonryCTACardProperties = {
  body: string;
  buttonLabel: string;
  buttonIndicator?: Indicator;
  eyebrow?: string;
  heading: string;
  image?: string;
  imageAltText?: string;
  isChildItem?: Boolean;
  showMobile?: Boolean;
};

export type MasonryCTACardNodeActions = {
  onButtonClick: MasonryEngineNodeAction;
  onDismiss?: MasonryDismissAction;
};

export type MasonryCTACardNode = MasonryEngineNode<
  'ctaCard',
  MasonryCTACardProperties,
  MasonryCTACardNodeActions
>;

export type MasonryCTACardRendererProps =
  MasonryEngineNodeRendererProps<MasonryCTACardNode>;

export const MasonryCTACardRenderer = ({
  css,
  body,
  buttonIndicator,
  buttonLabel,
  eyebrow,
  heading,
  image,
  imageAltText = '',
  isChildItem = false,
  onButtonClick,
  onDismiss,
  showMobile = false,
  rootElementRef,
  className,
  engineClassName,
  locatorId,
}: MasonryCTACardRendererProps) => {
  const isMobile = useMediaQuery(queryHelpers.only('mobile'));
  const showSmallerCard = isMobile || isChildItem || showMobile;

  const { invokeAction, isLoading: isClickInFlight } =
    useInvokeActionWithLoadingState();

  const buttonHref = (onButtonClick as MasonryDeeplinkAction)?.payload.url;

  return (
    <Card.Elevated
      data-testid={locatorId}
      css={{
        backgroundColor: '$surfaceBackgroundTertiary',
        padding: '$one',
        ...css,
      }}
      ref={rootElementRef}
      className={formatClassNames(engineClassName, className)}
    >
      {onDismiss && (
        <StackItem horizontalAlignment="end">
          <DismissButton onDismiss={onDismiss} />
        </StackItem>
      )}
      <StackLayout
        orientation="horizontal"
        verticalAlignment="center"
        spacing={{
          '@mobile': '$threeQuarters',
          '@initial': showSmallerCard ? '$threeQuarters' : '$oneAndHalf',
        }}
      >
        <StackLayout orientation="vertical" spacing="$quarter">
          {eyebrow && <UtilityText size="eyebrow">{eyebrow}</UtilityText>}
          <HeadingText level="3" size="sm">
            {heading}
          </HeadingText>
          <ParagraphText size="sm" htmlStringOptions={BodyTextHtmlTagOptions}>
            {body}
          </ParagraphText>

          {!showSmallerCard && (
            <Button
              indicator={
                buttonIndicator
                  ? getButtonIconToken(buttonIndicator)
                  : undefined
              }
              css={{ padding: '$half $two', marginTop: '$one' }}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...(buttonHref && {
                as: InternalOrExternalLink,
                disableDefaultOnClick: true,
              })}
              href={buttonHref}
              onClick={(event: React.MouseEvent) => {
                if (onButtonClick) {
                  invokeAction({
                    event,
                    ...onButtonClick,
                  });
                }
              }}
              // if the cta is not a url, we need to show the loading and disabled states
              loading={buttonHref ? undefined : isClickInFlight}
              disabled={buttonHref ? undefined : isClickInFlight}
            >
              {buttonLabel}
            </Button>
          )}
        </StackLayout>
        {image && (
          <SquareImageContainer
            src={image}
            alt={imageAltText}
            maxWidth="80px"
            maxWidthDesktop={isChildItem ? '80px' : '112px'}
            imageLoading="lazy"
          />
        )}
      </StackLayout>
      {showSmallerCard && (
        <Button
          width="fillContainer"
          indicator={
            buttonIndicator ? getButtonIconToken(buttonIndicator) : undefined
          }
          css={{ padding: '$half $two', marginTop: '$one' }}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...((onButtonClick as MasonryDeeplinkAction)?.payload.url && {
            as: InternalOrExternalLink,
            disableDefaultOnClick: true,
          })}
          href={buttonHref}
          onClick={(event: React.MouseEvent) => {
            if (onButtonClick) {
              invokeAction({
                event,
                ...onButtonClick,
              });
            }
          }}
          // if the cta is not a url, we need to show the loading and disabled states
          loading={buttonHref ? undefined : isClickInFlight}
          disabled={buttonHref ? undefined : isClickInFlight}
        >
          {buttonLabel}
        </Button>
      )}
    </Card.Elevated>
  );
};
